/**
 * "Discuss your needs" live-chat drawer.
 *
 * A right-hand `Sheet` that turns the top-bar CTA into an in-app Amazon Connect
 * chat instead of a marketing link. It has two phases:
 *
 *  1. A short contact form (name / email / subject / message), pre-filled from
 *     the Auth0 session so a signed-in user just hits "Start chat".
 *  2. A live transcript once the chat is connected — messages stream in via
 *     `amazon-connect-chatjs` and the user replies inline.
 *
 * The wiring lives in `@authe/fe-commons/connect`: `startChat` POSTs the form to
 * the PUBLIC, unauthenticated `aws-connect-mgmt` route (canonical `/connect/chat`,
 * routed by the shared `ApiClient` + `RESOURCE_REWRITES`) and returns a chatjs
 * handshake; the local `createChatSession` (`@/app/chat-session`) then lazily
 * loads chatjs and opens the websocket. The region chatjs talks to comes from the
 * handshake, so there is no extra env to configure here.
 */
import * as React from "react";
import { Loader2, Send } from "lucide-react";
import { useApiClient } from "@authe/ui-core/api";
import { useOptionalSession } from "@authe/ui-core/session";
import {
  Button,
  Input,
  Label,
  ScrollArea,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Textarea,
  notifyError,
} from "@authe/ui-core";
import {
  ContactSourcePlatform,
  startChat,
  StartChatInputSchema,
  type ChatJsSession,
  type StartChatInput,
} from "@authe/fe-commons/connect";
// chatjs loaded via a LITERAL dynamic import so Vite actually bundles it — the
// fe-commons `createChatSession` uses a variable specifier Vite can't bundle.
import { createChatSession } from "@/app/chat-session";

export interface ChatDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Phase = "form" | "starting" | "live" | "ended";

type Author = "customer" | "agent" | "system";

interface ChatMessage {
  id: string;
  author: Author;
  text: string;
  displayName?: string;
}

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const EMPTY_FORM: FormState = { name: "", email: "", subject: "", message: "" };

/** chatjs `event.data` is untyped; pull the few fields we render, defensively. */
function readIncoming(event: unknown): ChatMessage | null {
  const data = (event as { data?: Record<string, unknown> } | undefined)?.data;
  if (!data) return null;
  const type = String(data.Type ?? data.type ?? "MESSAGE").toUpperCase();
  if (type !== "MESSAGE") return null; // ignore typing / event frames here
  const content = (data.Content ?? data.content) as string | undefined;
  if (!content) return null;
  const role = String(data.ParticipantRole ?? data.participantRole ?? "AGENT").toUpperCase();
  const author: Author = role === "CUSTOMER" ? "customer" : role === "SYSTEM" ? "system" : "agent";
  const id = String(data.Id ?? data.id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const displayName = (data.DisplayName ?? data.displayName) as string | undefined;
  return { id, author, text: content, displayName };
}

export function ChatDrawer({ open, onOpenChange }: ChatDrawerProps): React.ReactElement {
  const client = useApiClient();
  const session = useOptionalSession()?.session ?? null;

  const [phase, setPhase] = React.useState<Phase>("form");
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [draft, setDraft] = React.useState("");
  const [sending, setSending] = React.useState(false);

  const chatRef = React.useRef<ChatJsSession | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // Prefill from the Auth0 session whenever the drawer opens fresh on the form.
  React.useEffect(() => {
    if (!open || phase !== "form") return;
    setForm((prev) => ({
      ...prev,
      name: prev.name || session?.user?.name || "",
      email: prev.email || session?.user?.email || "",
    }));
  }, [open, phase, session]);

  const appendMessage = React.useCallback((msg: ChatMessage) => {
    setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
  }, []);

  const teardown = React.useCallback(() => {
    const chat = chatRef.current;
    chatRef.current = null;
    if (chat) void chat.disconnectParticipant().catch(() => undefined);
  }, []);

  // Disconnect the websocket when the drawer unmounts.
  React.useEffect(() => teardown, [teardown]);

  // Auto-scroll the transcript to the newest message.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, phase]);

  const resetToForm = React.useCallback(() => {
    teardown();
    setMessages([]);
    setDraft("");
    setPhase("form");
  }, [teardown]);

  const handleClose = (next: boolean): void => {
    if (!next) {
      teardown();
      // Keep the form values but drop the live transcript on close.
      setMessages([]);
      setDraft("");
      setPhase("form");
    }
    onOpenChange(next);
  };

  const handleStart = async (): Promise<void> => {
    const parsed = StartChatInputSchema.safeParse({
      name: form.name,
      email: form.email,
      subject: form.subject || undefined,
      message: form.message || undefined,
      source_platform: ContactSourcePlatform.Dashboard,
    } satisfies Partial<StartChatInput>);
    if (!parsed.success) {
      notifyError(new Error(parsed.error.issues[0]?.message ?? "Check the form and try again."), {
        title: "Couldn't start chat",
      });
      return;
    }

    setPhase("starting");
    try {
      const handshake = await startChat(client, parsed.data);
      const chat = await createChatSession(handshake);
      chatRef.current = chat;

      chat.onMessage((event) => {
        const msg = readIncoming(event);
        if (msg) appendMessage(msg);
      });
      chat.onConnectionEstablished(() => setPhase("live"));
      chat.onConnectionBroken(() =>
        appendMessage({
          id: `sys-broken-${Date.now()}`,
          author: "system",
          text: "Connection lost — trying to reconnect…",
        }),
      );
      chat.onEnded(() => {
        appendMessage({ id: `sys-ended-${Date.now()}`, author: "system", text: "Chat ended." });
        setPhase("ended");
      });

      await chat.connect();
      setPhase("live");
    } catch (error) {
      notifyError(error, { title: "Couldn't start chat" });
      resetToForm();
    }
  };

  const handleSend = async (): Promise<void> => {
    const text = draft.trim();
    const chat = chatRef.current;
    if (!text || !chat || sending) return;
    setSending(true);
    setDraft("");
    try {
      await chat.sendMessage({ contentType: "text/plain", message: text });
    } catch (error) {
      setDraft(text); // restore the unsent draft
      notifyError(error, { title: "Message not sent" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border p-4 text-left">
          <SheetTitle>Discuss your needs</SheetTitle>
          <SheetDescription>
            Start a live chat with our team. We typically reply within a few minutes.
          </SheetDescription>
        </SheetHeader>

        {phase === "form" || phase === "starting" ? (
          <form
            className="flex flex-1 flex-col gap-4 overflow-y-auto p-4"
            onSubmit={(e) => {
              e.preventDefault();
              void handleStart();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="chat-name">Name</Label>
              <Input
                id="chat-name"
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                placeholder="Your name"
                autoComplete="name"
                disabled={phase === "starting"}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="chat-email">Email</Label>
              <Input
                id="chat-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@company.com"
                autoComplete="email"
                disabled={phase === "starting"}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="chat-subject">Subject</Label>
              <Input
                id="chat-subject"
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                placeholder="What's this about? (optional)"
                disabled={phase === "starting"}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="chat-message">Message</Label>
              <Textarea
                id="chat-message"
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                placeholder="Tell us a bit about what you're looking for (optional)"
                rows={4}
                disabled={phase === "starting"}
              />
            </div>

            <SheetFooter className="mt-auto">
              <Button type="submit" disabled={phase === "starting"} className="w-full">
                {phase === "starting" ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Connecting…
                  </>
                ) : (
                  "Start chat"
                )}
              </Button>
            </SheetFooter>
          </form>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden">
            <ScrollArea className="flex-1">
              <div ref={scrollRef} className="flex flex-col gap-2 p-4">
                {messages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    You&apos;re connected. Send a message to get started.
                  </p>
                ) : (
                  messages.map((m) => <MessageBubble key={m.id} message={m} />)
                )}
              </div>
            </ScrollArea>

            <div className="border-t border-border p-3">
              {phase === "ended" ? (
                <Button variant="outline" className="w-full" onClick={resetToForm}>
                  Start a new chat
                </Button>
              ) : (
                <form
                  className="flex items-end gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSend();
                  }}
                >
                  <Textarea
                    aria-label="Message"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void handleSend();
                      }
                    }}
                    placeholder="Type a message…"
                    rows={1}
                    className="max-h-32 min-h-[2.5rem] flex-1 resize-none"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    aria-label="Send message"
                    disabled={sending || !draft.trim()}
                  >
                    {sending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function MessageBubble({ message }: { message: ChatMessage }): React.ReactElement {
  if (message.author === "system") {
    return (
      <p className="self-center text-xs italic text-muted-foreground">{message.text}</p>
    );
  }
  const isCustomer = message.author === "customer";
  return (
    <div className={isCustomer ? "flex flex-col items-end" : "flex flex-col items-start"}>
      {!isCustomer && message.displayName ? (
        <span className="mb-0.5 text-xs text-muted-foreground">{message.displayName}</span>
      ) : null}
      <div
        className={
          "max-w-[80%] whitespace-pre-wrap break-words rounded-lg px-3 py-2 text-sm " +
          (isCustomer
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground")
        }
      >
        {message.text}
      </div>
    </div>
  );
}
